import type { LngLat } from "../adapters/types";

const EARTH_RADIUS_METERS = 6_371_008.8;

const toRad = (deg: number) => (deg * Math.PI) / 180;

/** Distance (mètres) entre deux points géographiques, sur une Terre sphérique (haversine). */
export function distanceMeters([lng1, lat1]: LngLat, [lng2, lat2]: LngLat): number {
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;

  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Longueur (mètres) d'une polyligne. */
export function polylineLengthMeters(points: LngLat[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) total += distanceMeters(points[i - 1]!, points[i]!);

  return total;
}

/** Anneau sans son point de fermeture dupliqué, s'il en a un. */
function openRing(ring: LngLat[]): LngLat[] {
  const first = ring[0];
  const last = ring[ring.length - 1];

  return ring.length > 1 && first && last && first[0] === last[0] && first[1] === last[1] ? ring.slice(0, -1) : ring;
}

/** Périmètre (mètres) d'un anneau, fermé ou non. */
export function ringPerimeterMeters(ring: LngLat[]): number {
  const points = openRing(ring);
  if (points.length < 2) return 0;

  return polylineLengthMeters([...points, points[0]!]);
}

/**
 * Surface (m²) d'un anneau, fermé ou non — projeté localement sur un plan (équirectangulaire autour de
 * sa latitude moyenne), largement assez précis à l'échelle d'un bâtiment ou d'un quartier.
 */
export function ringAreaSquareMeters(ring: LngLat[]): number {
  const points = openRing(ring);
  if (points.length < 3) return 0;

  const meanLat = points.reduce((sum, [, lat]) => sum + lat, 0) / points.length;
  const metersPerDegLat = (Math.PI / 180) * EARTH_RADIUS_METERS;
  const metersPerDegLng = metersPerDegLat * Math.cos(toRad(meanLat));
  const projected = points.map(([lng, lat]) => [lng * metersPerDegLng, lat * metersPerDegLat] as const);

  let twiceArea = 0;
  for (let i = 0; i < projected.length; i++) {
    const [x1, y1] = projected[i]!;
    const [x2, y2] = projected[(i + 1) % projected.length]!;
    twiceArea += x1 * y2 - x2 * y1;
  }

  return Math.abs(twiceArea) / 2;
}

const decimal = (value: number, digits: number) =>
  value.toLocaleString("fr-FR", { minimumFractionDigits: digits, maximumFractionDigits: digits });

/** « 12,4 m », « 245 m », « 1,25 km ». */
export function formatDistance(meters: number): string {
  if (meters >= 1000) return `${decimal(meters / 1000, 2)} km`;
  if (meters >= 100) return `${decimal(meters, 0)} m`;

  return `${decimal(meters, 1)} m`;
}

/** « 85 m² », « 1 250 m² », « 2,4 ha ». */
export function formatArea(squareMeters: number): string {
  if (squareMeters >= 10_000) return `${decimal(squareMeters / 10_000, 1)} ha`;

  return `${decimal(squareMeters, 0)} m²`;
}

/**
 * Étiquette d'un tracé point par point, avec le point qui suit le curseur : ligne — segment en cours
 * et longueur totale ; polygone — segment en cours et surface s'il était fermé là. `null` sans point posé.
 */
export function draftMeasureText(points: LngLat[], preview: LngLat, polygon: boolean): string | null {
  const last = points[points.length - 1];
  if (!last) return null;

  const segment = formatDistance(distanceMeters(last, preview));
  if (points.length < 2) return segment;
  if (polygon) return `${segment} · ${formatArea(ringAreaSquareMeters([...points, preview]))}`;

  return `${segment} · total ${formatDistance(polylineLengthMeters([...points, preview]))}`;
}

/** « 24 m × 12 m · 288 m² » pour les 4 coins d'un rectangle (dans l'ordre du contour). */
export function rectangleMeasureText(corners: LngLat[]): string | null {
  if (corners.length < 4) return null;

  const width = distanceMeters(corners[0]!, corners[1]!);
  const height = distanceMeters(corners[1]!, corners[2]!);

  return `${formatDistance(width)} × ${formatDistance(height)} · ${formatArea(ringAreaSquareMeters(corners))}`;
}

/** « rayon 12 m · 452 m² » pour un cercle donné par son centre et un point de son bord. */
export function circleMeasureText(center: LngLat, edge: LngLat): string {
  const radius = distanceMeters(center, edge);

  return `rayon ${formatDistance(radius)} · ${formatArea(Math.PI * radius ** 2)}`;
}
